import { Routes } from '@angular/router';
import { authGuard } from './core/auth/guards/auth.guard';
import { freshProjectResolver, projectSessionResolver } from './core/project-session/project-session.resolver';

/**
 * The wizard stages, in order.
 *
 * Declared once and mounted twice: under `/projects/:projectId` where the
 * resolver restores saved work first, and at the top level where it does not.
 * Defining them twice would mean a tenth stage silently working under one URL
 * and 404ing under the other.
 */
const stageRoutes: Routes = [
  {
    path: 'requirements-intake',
    title: 'Requirements Intake — AZ Symphony',
    loadComponent: () =>
      import('./features/intake-selector/intake-selector.component').then(
        (m) => m.IntakeSelectorComponent
      ),
  },
  {
    path: 'classification',
    title: 'Classification — AZ Symphony',
    loadComponent: () =>
      import('./features/classification/classification-page.component').then(
        (m) => m.ClassificationPageComponent
      ),
  },
  {
    path: 'backlog-review',
    title: 'Backlog Review — AZ Symphony',
    loadComponent: () =>
      import('./features/backlog-review/backlog-review-page.component').then(
        (m) => m.BacklogReviewPageComponent
      ),
  },
  {
    path: 'ui-feedback',
    title: 'UI Feedback — AZ Symphony',
    loadComponent: () =>
      import('./features/ui-feedback/ui-feedback-page.component').then(
        (m) => m.UiFeedbackPageComponent
      ),
  },
  {
    path: 'architecture',
    title: 'Architecture — AZ Symphony',
    loadComponent: () =>
      import('./features/architecture/architecture-page.component').then(
        (m) => m.ArchitecturePageComponent
      ),
  },
  {
    path: 'agentified-build',
    title: 'Agentified Build — AZ Symphony',
    loadComponent: () =>
      import('./features/agentified-build/agentified-build-page.component').then(
        (m) => m.AgentifiedBuildPageComponent
      ),
  },
  {
    path: 'quality-control',
    title: 'Quality Control — AZ Symphony',
    loadComponent: () =>
      import('./features/quality-control/quality-control-page.component').then(
        (m) => m.QualityControlPageComponent
      ),
  },
  {
    path: 'costs-budget',
    title: 'Costs and Budget — AZ Symphony',
    loadComponent: () =>
      import('./features/costs-budget/costs-budget-page.component').then(
        (m) => m.CostsBudgetPageComponent
      ),
  },
  {
    path: 'deploy',
    title: 'Deploy — AZ Symphony',
    loadComponent: () =>
      import('./features/deploy/deploy-page.component').then(
        (m) => m.DeployPageComponent
      ),
  },
];

export const routes: Routes = [
  {
    path: '',
    pathMatch: 'full',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/home/home-page.component').then((m) => m.HomePageComponent),
    title: 'Home — AZ Symphony',
  },
  {
    path: 'projects/:projectId',
    canActivate: [authGuard],
    resolve: { project: projectSessionResolver },
    children: [
      { path: '', pathMatch: 'full', redirectTo: 'requirements-intake' },
      ...stageRoutes,
    ],
  },
  {
    path: 'dashboard',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./core/layout/pages/dashboard/dashboard.component').then(
        (m) => m.DashboardComponent
      ),
    title: 'Dashboard — AZ Symphony',
  },
  // The same stages without a project. A new intake starts here and moves to
  // the prefixed URLs once the backend has given it a project id.
  ...stageRoutes.map((route) => ({
    ...route,
    canActivate: [authGuard],
    // Only the entry point needs to clear a stale project id — the other
    // unprefixed stages are reached later in the SAME not-yet-persisted
    // intake, where resetting would wipe the analysis just computed.
    ...(route.path === 'requirements-intake' ? { resolve: { fresh: freshProjectResolver } } : {}),
  })),
  {
    path: 'auth',
    title: 'Sign in — AZ Symphony',
    loadComponent: () =>
      import('./core/auth/pages/login/login.component').then(
        (m) => m.LoginComponent
      ),
  },
  {
    path: 'auth/callback',
    title: 'Authentication callback — AZ Symphony',
    loadComponent: () =>
      import('./core/auth/pages/callback/callback.component').then(
        (m) => m.CallbackComponent
      ),
  },
  {
    path: 'auth/logout',
    title: 'Signing out — AZ Symphony',
    loadComponent: () =>
      import('./core/auth/pages/logout/logout.component').then(
        (m) => m.LogoutComponent
      ),
  },
  {
    path: 'auth/redirect',
    title: 'Redirecting — AZ Symphony',
    loadComponent: () =>
      import('./core/auth/pages/redirect/redirect.component').then(
        (m) => m.RedirectComponent
      ),
  },
  { path: '**', redirectTo: 'auth', pathMatch: 'full', title: 'AZ Symphony' },
];
